import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronRight, ChevronDown, Trash, Plane, ArrowRight, MoreVertical, X } from 'lucide-react';
import { FlightInfo } from './types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatFlightDate, formatFlightTime, formatFlightFullDateTime } from '@/lib/flight-datetime-utils';

// Cache global para armazenar informações de voos por ID
// Isso ajuda a manter as informações entre renderizações e mudanças de página
const flightCache: Record<string, FlightInfo> = {};

// Extend FlightInfo to include pageName, which will be added in UnifiedItemsView
type FlightInfoWithPage = FlightInfo & { pageName?: string };

interface FlightEventFormProps {
  flightEvent: {
    id: string;
    title: string;
    flightId: string;
    isDeparture: boolean;
    _coordinates?: {
      lat: number;
      lng: number;
    };
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  index: number;
  pageId: string;
  flights: FlightInfoWithPage[]; // Use the extended type
  updateFlightEvent: (pageId: string, flightEventId: string, field: string, value: any) => void;
  deleteFlightEvent: (pageId: string, flightEventId: string) => void;
  moveFlightEvent?: (flightEventId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  allPages?: any[]; // Keep allPages if needed elsewhere, added in UnifiedItemsView
}

export function FlightEventForm({
  flightEvent,
  index,
  pageId,
  flights,
  updateFlightEvent,
  deleteFlightEvent,
  moveFlightEvent,
  isCollapsed,
  onToggleCollapse
}: FlightEventFormProps) {
  // Find the initial flight state immediately on render, first from cache, then from props
  const cachedFlight = flightEvent.flightId ? flightCache[flightEvent.flightId] : null;
  const matchingFlight = flightEvent.flightId ? flights.find(f => f.id === flightEvent.flightId) : null;
  const initialFlight = cachedFlight || matchingFlight || null;
  
  // Se encontramos o voo, atualizar o cache
  if (matchingFlight) {
    flightCache[matchingFlight.id] = matchingFlight;
  }
  
  // Log para debug do carregamento inicial
  console.log('[FlightEventForm] Inicializando com:', {
    flightEventId: flightEvent.id,
    flightId: flightEvent.flightId,
    initialFlightFound: Boolean(initialFlight),
    usingCachedFlight: Boolean(cachedFlight && initialFlight === cachedFlight),
    totalFlights: flights.length,
    hasCoordinates: Boolean(flightEvent._coordinates),
  });
  
  // Store flights and flightEvent in refs to prevent unnecessary renders
  const flightsRef = useRef(flights);
  const flightEventRef = useRef(flightEvent);
  const [selectedFlight, setSelectedFlight] = useState<FlightInfoWithPage | null>(initialFlight);
  const [flightSearchValue, setFlightSearchValue] = useState<string>('');
  const prevFlightIdRef = useRef<string | null>(flightEvent.flightId || null);
  const mountedRef = useRef(false);
  const updateTitleRef = useRef(false);
  
  // Use a state to track if we need to force fetch flight data
  // const [needsForcedFlightUpdate, setNeedsForcedFlightUpdate] = useState<boolean>(false); // Comentado - lógica simplificada

  // Update refs when props change
  useEffect(() => {
    flightsRef.current = flights;
    flightEventRef.current = flightEvent;
    
    // Se o flightId mudou externamente, tentar buscar o voo
    if (flightEvent.flightId && flightEvent.flightId !== prevFlightIdRef.current) {
      console.log(`[FlightEventForm] flightId prop changed to ${flightEvent.flightId}. Searching for flight...`);
      const matchingFlight = flights.find(f => f.id === flightEvent.flightId);
      if (matchingFlight) {
        console.log('[FlightEventForm] Found matching flight from props change:', matchingFlight.id);
        setSelectedFlight(matchingFlight);
        prevFlightIdRef.current = flightEvent.flightId;
      } else {
        console.warn('[FlightEventForm] Flight not found immediately after prop change for ID:', flightEvent.flightId);
        // Considerar tentar buscar no cache aqui também, se necessário
        setSelectedFlight(null); // Limpar se não encontrado
        prevFlightIdRef.current = flightEvent.flightId;
      }
    } else if (!flightEvent.flightId && selectedFlight) {
      // Limpar se o flightId foi removido externamente
      console.log('[FlightEventForm] flightId prop removed. Clearing selected flight.');
      setSelectedFlight(null);
      prevFlightIdRef.current = null;
    }
    
  }, [flights, flightEvent.flightId]); // Remover selectedFlight daqui
  
  // Quando selectedFlight muda, atualizar o cache
  useEffect(() => {
    if (selectedFlight) {
      flightCache[selectedFlight.id] = selectedFlight;
    }
  }, [selectedFlight]);
  
  // Effect to handle forcing flight update when needed - REMOVIDO/SIMPLIFICADO
  /*
  useEffect(() => {
    // ... código removido ...
  }, [needsForcedFlightUpdate, flightEvent.flightId, flights]);
  */

  // This effect handles direct updates to the flight ID - REMOVIDO/SIMPLIFICADO
  /*
  useEffect(() => {
    // ... código removido ...
  }, [flightEvent.flightId, flights]);
  */

  // Função centralizada para atualizar as coordenadas no flightEvent
  const updateEventCoordinates = useCallback((flight: FlightInfoWithPage | null, isDeparture: boolean) => {
    if (!flight) {
      console.log('[FlightEventForm] No selected flight, clearing coordinates.');
      updateFlightEvent(pageId, flightEventRef.current.id, '_coordinates', null);
      updateFlightEvent(pageId, flightEventRef.current.id, 'coordinates', null);
      return;
    }

    const targetCoordinates = isDeparture
      ? flight.departureCoordinates
      : flight.arrivalCoordinates;

    // Validação básica de coordenadas
    const hasValidCoordinates = targetCoordinates && 
                                typeof targetCoordinates.lat === 'number' && 
                                typeof targetCoordinates.lng === 'number' &&
                                !isNaN(targetCoordinates.lat) && 
                                !isNaN(targetCoordinates.lng);

    const newCoordinates = hasValidCoordinates ? targetCoordinates : null;
    const currentCoords = flightEventRef.current._coordinates || flightEventRef.current.coordinates;
    
    // Atualizar apenas se as coordenadas mudaram
    const needsUpdate = (!currentCoords && newCoordinates) || 
                      (currentCoords && !newCoordinates) || 
                      (currentCoords && newCoordinates && (currentCoords.lat !== newCoordinates.lat || currentCoords.lng !== newCoordinates.lng));

    if (needsUpdate) {
      console.log('[FlightEventForm] Updating coordinates via updateEventCoordinates:', {
        flightEventId: flightEventRef.current.id,
        flightId: flight.id,
        newCoordinates,
        isDeparture
      });
      updateFlightEvent(pageId, flightEventRef.current.id, '_coordinates', newCoordinates);
      updateFlightEvent(pageId, flightEventRef.current.id, 'coordinates', newCoordinates);
    } else {
       console.log('[FlightEventForm] Coordinates are already up-to-date. No update needed.');
    }
  }, [pageId, updateFlightEvent]); // A dependência de flightEvent foi removida daqui e usada via ref

  // Efeito para atualizar coordenadas quando o voo selecionado OU isDeparture mudar
  useEffect(() => {
    console.log('[FlightEventForm] useEffect for coordinate update triggered. Selected flight:', selectedFlight?.id, 'isDeparture:', flightEvent.isDeparture);
    updateEventCoordinates(selectedFlight, flightEvent.isDeparture);
  }, [selectedFlight, flightEvent.isDeparture, updateEventCoordinates]);

  // This effect handles title updates after flight changes
  useEffect(() => {
    // Only update title if flag is set and we have a selected flight
    if (!updateTitleRef.current || !selectedFlight) {
      return;
    }

    // If we need to update the title and we're not auto-updating titles anymore
    if (!flightEvent.title && !mountedRef.current) {
      const eventType = flightEvent.isDeparture ? 'Saída' : 'Chegada';
      
      // For arrival events with connections, use the last connection info
      if (!flightEvent.isDeparture && selectedFlight.connections && selectedFlight.connections.length > 0) {
        const lastConnection = selectedFlight.connections[selectedFlight.connections.length - 1];
        const airline = lastConnection.airline || selectedFlight.airline || '';
        const flightNumber = lastConnection.flightNumber || selectedFlight.flightNumber || '';
        const newTitle = `${eventType} - ${airline} ${flightNumber}`;
        updateFlightEvent(pageId, flightEvent.id, 'title', newTitle);
      } else {
        // For departure events or arrivals without connections
        const newTitle = `${eventType} - ${selectedFlight.airline || ''} ${selectedFlight.flightNumber || ''}`;
        updateFlightEvent(pageId, flightEvent.id, 'title', newTitle);
      }
      
      // Mark as mounted to prevent title updates on subsequent renders
      mountedRef.current = true;
    }
    
    // Reset the flag
    updateTitleRef.current = false;
  }, [selectedFlight, flightEvent.id, flightEvent.isDeparture, flightEvent.title, pageId, updateFlightEvent]);

  // Effect to update coordinates when flight or isDeparture changes - REMOVIDO (agora centralizado acima)
  /*
  useEffect(() => {
    // ... código removido ...
  }, [selectedFlight, flightEvent.isDeparture, flightEvent.id, pageId, updateFlightEvent, flightEvent._coordinates, flightEvent.coordinates]);
  */

  // Atualizar coordenadas quando o tipo (saída/chegada) for alterado
  const handleIsDepartureChange = (checked: boolean) => {
    console.log(`[FlightEventForm] Changing isDeparture to: ${checked}`);
    updateFlightEvent(pageId, flightEvent.id, 'isDeparture', checked);
    // Não precisa chamar updateEventCoordinates aqui, o useEffect acima cuidará disso
  };

  // Lógica para seleção de voo
  const handleFlightChange = (flightId: string) => {
    if (!flightId) {
      console.log('[FlightEventForm] Clearing selected flight via dropdown.');
      setSelectedFlight(null);
      updateFlightEvent(pageId, flightEvent.id, 'flightId', '');
      // O useEffect cuidará da atualização das coordenadas
      return;
    }
    
    const flight = flightsRef.current.find(f => f.id === flightId);
    if (flight) {
      console.log('[FlightEventForm] Flight selected via dropdown:', flight.id, flight.pageName);
    setSelectedFlight(flight);
      updateFlightEvent(pageId, flightEvent.id, 'flightId', flight.id);
      prevFlightIdRef.current = flight.id; // Atualizar ref
      // O useEffect cuidará da atualização das coordenadas
      
      // Marcar para atualizar o título se necessário
      updateTitleRef.current = true;
    } else {
      console.warn('[FlightEventForm] Selected flight not found in list:', flightId);
      // Limpar seleção se não encontrar
      setSelectedFlight(null);
      updateFlightEvent(pageId, flightEvent.id, 'flightId', '');
    }
  };

  // Function to get the last connection if needed
  const getLastConnection = useCallback((flight: FlightInfo) => {
    if (flight.connections && flight.connections.length > 0) {
      return flight.connections[flight.connections.length - 1];
    }
    return null;
  }, []);

  // Function to get the appropriate airport for arrival
  const getArrivalAirport = useCallback((flight: FlightInfo, isDeparture: boolean) => {
    if (!isDeparture) {
      const lastConnection = getLastConnection(flight);
      if (lastConnection) {
        return lastConnection.arrivalAirport || 'Não informado';
      }
    }
    return flight.arrivalAirport || 'Não informado';
  }, [getLastConnection]);

  // Helper function to format flight datetime in original timezone
  const formatFlightDateTimeDisplay = (isoDateTimeString?: string): string => {
    if (!isoDateTimeString) return 'Não informado';
    return `${formatFlightDate(isoDateTimeString)} às ${formatFlightTime(isoDateTimeString)}`;
  };

  // Helper function to get arrival datetime
  const getArrivalDateTime = () => {
    if (!selectedFlight) return 'Não informado';
    
    if (!flightEvent.isDeparture && selectedFlight.connections && selectedFlight.connections.length > 0) {
      const lastConnection = selectedFlight.connections[selectedFlight.connections.length - 1];
      return formatFlightDateTimeDisplay(lastConnection.arrivalDateTime);
    }
    
    return formatFlightDateTimeDisplay(selectedFlight.arrivalDateTime);
  };

  // Function to get appropriate flight duration
  const getFlightDuration = useCallback((flight: FlightInfo, isDeparture: boolean) => {
    if (!isDeparture) {
      const lastConnection = getLastConnection(flight);
      if (lastConnection) {
        return lastConnection.flightDuration || 'Não informado';
      }
    }
    return flight.flightDuration || 'Não informado';
  }, [getLastConnection]);

  // Memoize the flight information section to prevent re-renders
  const flightInfoSection = React.useMemo(() => {
    if (!selectedFlight) return null;
    
    const hasConnections = selectedFlight.connections && selectedFlight.connections.length > 0;
    const showConnectionIndicator = !flightEvent.isDeparture && hasConnections;
    
    // Extrair informações de coordenadas para debug
    const departureCoordinates = selectedFlight.departureCoordinates 
      ? `${selectedFlight.departureCoordinates.lat}, ${selectedFlight.departureCoordinates.lng}` 
      : 'Não disponíveis';
    
    const arrivalCoordinates = selectedFlight.arrivalCoordinates 
      ? `${selectedFlight.arrivalCoordinates.lat}, ${selectedFlight.arrivalCoordinates.lng}` 
      : 'Não disponíveis';
      
    // Verificar se o flightEvent tem coordenadas próprias
    const flightEventCoordinates = flightEvent._coordinates 
      ? `${flightEvent._coordinates.lat}, ${flightEvent._coordinates.lng}`
      : 'Não disponíveis';
    
    return (
      <div className="space-y-2">
        <Label>Informações do Voo</Label>
        
        {/* Show connection indicator if available */}
        {showConnectionIndicator && (
          <div className="mb-2 text-xs text-blue-600 flex items-center">
            <span className="font-medium flex items-center">
              <Plane className="h-3 w-3 mr-1" /> 
              Mostrando informações do último voo da conexão
            </span>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-gray-500">Aeroporto de Saída</Label>
            <p className="text-sm">{selectedFlight.departureAirport || 'Não informado'}</p>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Aeroporto de Chegada</Label>
            <p className="text-sm">{getArrivalAirport(selectedFlight, flightEvent.isDeparture)}</p>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Horário de Saída</Label>
            <p className="text-sm">
              {formatFlightDateTimeDisplay(selectedFlight.departureDateTime)}
            </p>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Horário de Chegada</Label>
            <p className="text-sm">{getArrivalDateTime()}</p>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Duração do Voo</Label>
            <p className="text-sm">{getFlightDuration(selectedFlight, flightEvent.isDeparture)}</p>
          </div>
        </div>
        
        {/* Seção de Debug para Coordenadas */}
        <div className="mt-4 p-3 bg-gray-100 rounded-md">
          <h4 className="text-xs font-semibold text-gray-700 mb-2">Informações de Coordenadas (Debug)</h4>
          <div className="grid grid-cols-1 gap-2 text-xs">
            <div>
              <span className="font-medium">Flight ID:</span> {selectedFlight.id}
            </div>
            <div>
              <span className="font-medium">Coordenadas de Partida:</span> {departureCoordinates}
            </div>
            <div>
              <span className="font-medium">Coordenadas de Chegada:</span> {arrivalCoordinates}
            </div>
            <div>
              <span className="font-medium">Coordenadas do FlightEvent:</span> {flightEventCoordinates}
            </div>
            <div>
              <span className="font-medium">Coordenadas Usadas:</span> {flightEvent.isDeparture ? departureCoordinates : arrivalCoordinates}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    selectedFlight, 
    flightEvent.isDeparture, 
    flightEvent._coordinates, 
    getArrivalAirport, 
    getArrivalDateTime, 
    getFlightDuration
  ]);

  // Eagerly compute the heading text to avoid flashing
  const headingText = flightEvent.title || (flightEvent.isDeparture ? 'Saída' : 'Chegada');
  const showConnectionLabel = !flightEvent.isDeparture && selectedFlight?.connections?.length > 0 && !flightEvent.title;

  // Simplified display function for flight details
  const getFlightDisplay = (flight: FlightInfoWithPage | null) => {
    if (!flight) return 'N/A';
    return `${flight.airline || ''} ${flight.flightNumber || ''} (${flight.departureAirport || ''} -> ${flight.arrivalAirport || ''})`;
  };

  return (
    <Card className={cn("mb-4 overflow-hidden transition-all duration-300 ease-in-out", isCollapsed ? 'max-h-16' : 'max-h-full')}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gray-50 border-b cursor-pointer" onClick={onToggleCollapse}>
        <div className="flex items-center gap-3 flex-grow min-w-0">
          {isCollapsed ? <ChevronRight className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
          <Plane className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div className="flex-grow min-w-0">
            <span className="font-medium text-sm truncate block" title={flightEvent.title || 'Evento de Voo'}>
              {flightEvent.title || 'Evento de Voo'}
            </span>
            {selectedFlight && (
              <span className="text-xs text-gray-500 truncate block" title={getFlightDisplay(selectedFlight)}>
                {getFlightDisplay(selectedFlight)}
          </span>
            )}
            {!selectedFlight && flightEvent.flightId && (
              <span className="text-xs text-red-500 truncate block">Voo vinculado não encontrado</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {moveFlightEvent && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); moveFlightEvent(flightEvent.id); }}>
                  Mover para outra página
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); deleteFlightEvent(pageId, flightEvent.id); }} className="text-red-600">
                <Trash className="mr-2 h-4 w-4" />
                Excluir Evento
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Content - Rendered only when not collapsed */}
      {!isCollapsed && (
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`flight-event-title-${flightEvent.id}`}>Título do Evento</Label>
            <Input
                id={`flight-event-title-${flightEvent.id}`}
                value={flightEvent.title}
                onChange={(e) => updateFlightEvent(pageId, flightEvent.id, 'title', e.target.value)}
                placeholder="Ex: Saída voo principal"
            />
          </div>

            <div>
              <Label htmlFor={`flight-select-${flightEvent.id}`}>Vincular ao Voo</Label>
            <Select
                value={flightEvent.flightId || ''}
              onValueChange={handleFlightChange}
            >
                <SelectTrigger id={`flight-select-${flightEvent.id}`}>
                <SelectValue placeholder="Selecione um voo" />
              </SelectTrigger>
              <SelectContent>
                {flights.map((flight) => (
                  <SelectItem key={flight.id} value={flight.id}>
                      {flight.airline} {flight.flightNumber} ({flight.departureAirport}-{flight.arrivalAirport})
                      {flight.pageName && <span className="text-xs text-gray-500 ml-1">({flight.pageName})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor={`event-type-switch-${flightEvent.id}`} className="flex flex-col space-y-1">
              <span>Tipo de Evento</span>
              <span className="font-normal leading-snug text-muted-foreground">
                {flightEvent.isDeparture ? 'Saída (Departure)' : 'Chegada (Arrival)'}
              </span>
            </Label>
            <Switch
              id={`event-type-switch-${flightEvent.id}`}
              checked={flightEvent.isDeparture}
              onCheckedChange={handleIsDepartureChange}
            />
          </div>

          {selectedFlight && (
            <div className="text-sm text-gray-600 border-t pt-4 mt-4">
              <p><strong>Detalhes do Voo Vinculado:</strong></p>
              <p><strong>Companhia:</strong> {selectedFlight.airline}</p>
              <p><strong>Número:</strong> {selectedFlight.flightNumber}</p>
              <p><strong>Rota:</strong> {selectedFlight.departureAirport} <ArrowRight className="inline h-4 w-4"/> {selectedFlight.arrivalAirport}</p>
              <p><strong>Saída:</strong> {formatFlightFullDateTime(selectedFlight.departureDateTime)}</p>
              <p><strong>Chegada:</strong> {formatFlightFullDateTime(selectedFlight.arrivalDateTime)}</p>
              {/* Exibir página do voo */}
              {(selectedFlight as FlightInfoWithPage).pageName && (
                <p><strong>Página:</strong> {(selectedFlight as FlightInfoWithPage).pageName}</p>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
} 